import React, { useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import translations from '../data/translations';
import { motion } from 'framer-motion';
import { Send, CheckCircle } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ContactSection() {
  const { lang } = useLanguage();
  const t = translations.contact[lang];
  const [form, setForm] = useState({ name: '', email: '', company: '', phone: '', service: '', message: '' });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      await axios.post(`${API}/contact`, { ...form, language: lang });
      setSuccess(true);
      setForm({ name: '', email: '', company: '', phone: '', service: '', message: '' });
    } catch (err) {
      setError(lang === 'de' ? 'Fehler beim Senden. Bitte versuchen Sie es erneut.' : 'Errore nell\'invio. Riprovare.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="relative py-24 md:py-32 overflow-hidden">
      {/* Navy diagonal background */}
      <div className="absolute inset-0 bg-enbi-navy" />
      <div
        className="absolute top-0 right-0 w-[50%] h-full bg-enbi-gold/[0.06]"
        style={{ clipPath: 'polygon(30% 0, 100% 0, 100% 100%, 0 100%)' }}
      />

      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
          {/* Left info */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-5"
          >
            <div className="flex items-center gap-4 mb-6">
              <div className="w-10 h-[2px] bg-enbi-gold" />
              <span className="text-enbi-gold text-xs tracking-[0.3em] uppercase font-semibold">{t.label}</span>
            </div>
            <h2 className="font-outfit text-2xl sm:text-3xl lg:text-4xl font-semibold text-white uppercase tracking-tight mb-6">
              {t.headline}
            </h2>
            <p className="text-white/60 text-base leading-relaxed mb-10">
              {t.sub}
            </p>

            <div className="space-y-6">
              <div className="flex items-center gap-4">
                <img src="/logo-monogram-white.png" alt="ENBI" className="w-16 h-16 opacity-80" />
                <div>
                  <div className="text-white font-outfit font-bold tracking-wider">ENBI MARKETING</div>
                  <div className="text-enbi-gold text-xs tracking-[0.2em] uppercase">Digital Media Agency</div>
                  <a href="mailto:info@enbi-marketing.de" className="text-white/60 text-sm hover:text-enbi-gold transition-colors mt-2 block">info@enbi-marketing.de</a>
                </div>
              </div>

              {/* Social Media Icons */}
              <div className="flex items-center gap-4 ml-20">
                <a href="https://www.facebook.com/enbimarketing" target="_blank" rel="noopener noreferrer" className="w-10 h-10 border border-white/20 flex items-center justify-center hover:border-enbi-gold hover:text-enbi-gold text-white/50 transition-colors" data-testid="social-facebook">
                  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"/></svg>
                </a>
                <a href="https://www.instagram.com/enbi.marketing/" target="_blank" rel="noopener noreferrer" className="w-10 h-10 border border-white/20 flex items-center justify-center hover:border-enbi-gold hover:text-enbi-gold text-white/50 transition-colors" data-testid="social-instagram">
                  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><rect width="20" height="20" x="2" y="2" rx="5" ry="5"/><path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"/><line x1="17.5" x2="17.51" y1="6.5" y2="6.5"/></svg>
                </a>
                <a href="https://www.linkedin.com/in/niccol%C3%B2-danilo-tardo-73aa30117" target="_blank" rel="noopener noreferrer" className="w-10 h-10 border border-white/20 flex items-center justify-center hover:border-enbi-gold hover:text-enbi-gold text-white/50 transition-colors" data-testid="social-linkedin">
                  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z"/><rect width="4" height="12" x="2" y="9"/><circle cx="4" cy="4" r="2"/></svg>
                </a>
              </div>
            </div>
          </motion.div>

          {/* Right form */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="lg:col-span-7"
          >
            {success ? (
              <div className="bg-white/[0.05] border border-enbi-gold/30 p-12 text-center" data-testid="contact-success">
                <CheckCircle className="text-enbi-gold mx-auto mb-4" size={48} strokeWidth={1.25} />
                <p className="text-white text-lg font-outfit">{t.success}</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-5" data-testid="contact-form">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <input
                    name="name"
                    value={form.name}
                    onChange={handleChange}
                    required
                    placeholder={t.name}
                    data-testid="contact-name"
                    className="bg-white/[0.06] border border-white/15 px-5 py-4 text-white text-sm placeholder:text-white/35 focus:border-enbi-gold focus:outline-none transition-colors"
                  />
                  <input
                    name="email"
                    type="email"
                    value={form.email}
                    onChange={handleChange}
                    required
                    placeholder={t.email}
                    data-testid="contact-email"
                    className="bg-white/[0.06] border border-white/15 px-5 py-4 text-white text-sm placeholder:text-white/35 focus:border-enbi-gold focus:outline-none transition-colors"
                  />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <input
                    name="company"
                    value={form.company}
                    onChange={handleChange}
                    placeholder={t.company}
                    data-testid="contact-company"
                    className="bg-white/[0.06] border border-white/15 px-5 py-4 text-white text-sm placeholder:text-white/35 focus:border-enbi-gold focus:outline-none transition-colors"
                  />
                  <input
                    name="phone"
                    value={form.phone}
                    onChange={handleChange}
                    placeholder={t.phone}
                    data-testid="contact-phone"
                    className="bg-white/[0.06] border border-white/15 px-5 py-4 text-white text-sm placeholder:text-white/35 focus:border-enbi-gold focus:outline-none transition-colors"
                  />
                </div>
                <select
                  name="service"
                  value={form.service}
                  onChange={handleChange}
                  data-testid="contact-service"
                  className="w-full bg-white/[0.06] border border-white/15 px-5 py-4 text-sm text-white/60 focus:border-enbi-gold focus:outline-none transition-colors appearance-none"
                >
                  <option value="" className="bg-enbi-navy">{t.servicePlaceholder}</option>
                  {t.serviceOptions.map((opt, i) => (
                    <option key={i} value={opt} className="bg-enbi-navy text-white">{opt}</option>
                  ))}
                </select>
                <textarea
                  name="message"
                  value={form.message}
                  onChange={handleChange}
                  required
                  rows={5}
                  placeholder={t.message}
                  data-testid="contact-message"
                  className="w-full bg-white/[0.06] border border-white/15 px-5 py-4 text-white text-sm placeholder:text-white/35 focus:border-enbi-gold focus:outline-none transition-colors resize-none"
                />
                {error && <p className="text-red-400 text-sm">{error}</p>}
                <button
                  type="submit"
                  disabled={loading}
                  data-testid="contact-submit"
                  className="bg-enbi-gold text-enbi-navy px-10 py-4 text-xs tracking-[0.2em] uppercase font-bold hover:bg-enbi-gold-light transition-all duration-300 hover:scale-[1.02] disabled:opacity-50 flex items-center gap-3"
                >
                  <Send size={16} strokeWidth={1.5} />
                  {loading ? '...' : t.submit}
                </button>
              </form>
            )}
          </motion.div>
        </div>
      </div>
    </section>
  );
}
